import { RouteComponentProps } from "../../../../../models/RouteComponent";
import useApiService from "../../../../../hooks/useApiService";
import { ChecklistRecord, EquipmentRecord } from "../../../../../models/DSF/Equipment";
import React, { useEffect, useRef, useState } from "react";
import { CardActions, CardOverflow, Typography } from "@mui/joy";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CancelIcon, CheckIcon, ProgressActivity } from "../../../../../assets/icons";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import "../shared/styles.scss";
import { displayContent } from "../../../../../utils/generalUtils";
import { CompanyDocumentRecord } from "../../../../../models/DSF/CompanyDocuments";
import { ChemicalRecord } from "../../../../../models/DSF/Chemicals";
import Documents from "../shared/Documents";

function ChemicalListAndChecklist(props: RouteComponentProps) {
  const { session } = props;
  const [chemicalId, _] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [checklist, setChecklist] = useState<ChecklistRecord | null>(null);
  const [documents, setDocuments] = useState<CompanyDocumentRecord[]>([]);

  // TODO: Temp fix until equipment details endpoint returns correct response
  const [chemicalData, setChemicalData] = useState<ChemicalRecord | null>(null);

  const toast = useRef<any>(null);

  const { data, error, loading } = useApiService<ChemicalRecord[]>({
    method: "GET",
    url: "site-chemicals",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
  });

  const {
    fetchData: fetchChecklists,
    loading: loadingChecklist,
    error: checklistError,
  } = useApiService<ChecklistRecord>({
    url: `site-chemicals/${chemicalId}/checklists`,
    fetchOnMount: false,
    method: "GET",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
    onSuccess: data => {
      setChecklist(data);
    },
  });

  const {
    fetchData: fetchDocuments,
    loading: loadingDocuments,
    error: errorDocuments,
  } = useApiService<CompanyDocumentRecord[]>({
    url: `site-chemicals/${chemicalId}/documents`,
    method: "GET",
    apiName: "open",
    params: {
      siteId: session.site_id,
      clientId: session.client_id,
    },
    fetchOnMount: false,
    onSuccess: data => {
      setDocuments(data);
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading]);

  const handleTabChange = async (e: any) => {
    setActiveIndex(e.index);
    if (e.index === 1 && !checklist) {
      fetchChecklists({ url: `site-chemicals/${chemicalData?.id}/checklists` });
    }
    if (e.index === 2 && documents.length === 0) {
      fetchDocuments({ url: `site-chemicals/${chemicalData?.id}/documents` });
    }
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Chemical list & checklist
      </Typography>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions
          style={{ marginTop: 15, marginBottom: 15 }}
          sx={{ alignSelf: "flex-end", pt: 2 }}
        >
          <Button
            name="button"
            icon="pi pi-save"
            label="Download PDF"
            className="p-button-outlined"
            size="small"
            disabled={true}
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => {}}
          />
        </CardActions>
      </CardOverflow>
      {error ? (
        <div className={"error-wrapper"}>
          <h2>Error fetching tool list</h2>
        </div>
      ) : (
        <>
          {data && (
            <DataTable
              value={data}
              onRowClick={e => {
                setChemicalData(e.data as ChemicalRecord);
              }}
              className={"policies-procedures-table"}
            >
              <Column field={"name"} header={"Name"} />
              <Column field={"description"} header={"Description"} />
              <Column field={"chemical_type_name"} header={"Chemical type"} />
            </DataTable>
          )}
        </>
      )}
      {chemicalData && (
        <Dialog
          onHide={() => {
            setChemicalData(null);
            setChecklist(null);
            setActiveIndex(0);
            setDocuments([]);
          }}
          visible={!!chemicalData}
          header={chemicalData.name}
          style={{ width: "60vw" }}
        >
          <TabView onTabChange={handleTabChange} activeIndex={activeIndex}>
            <TabPanel header={"Equipment details"} key={"details"}>
              <div className={"equipment-leading-content shadow"}>
                <img
                  className={"equipment-photo"}
                  alt="Silhouette of mountains"
                  src={
                    chemicalData?.photo ||
                    "https://ionicframework.com/docs/img/demos/card-media.png"
                  }
                  onError={e => {
                    e.currentTarget.src =
                      "https://ionicframework.com/docs/img/demos/card-media.png";
                  }}
                />

                <div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Quantity:</label>
                    <span>{displayContent(chemicalData.quantity)}</span>
                  </div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Units:</label>
                    <span>{displayContent(chemicalData.units)}</span>
                  </div>
                  <div className={"key-value"}>
                    <label className={"font-bold mr-4"}>Description:</label>
                    <span>{displayContent(chemicalData.description)}</span>
                  </div>
                </div>
              </div>
              <div className={"shadow maintenance-content"}>
                <div className={"maintenance-section-header maintenance-section-header-darker"}>
                  Chemical details
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Name</p>
                  <p>{displayContent(chemicalData.name)}</p>
                </div>
                <div className={"responsive-grid-2"}>
                  <p>Chemical type</p>
                  <p>{displayContent(chemicalData.chemical_type_name)}</p>
                </div>
              </div>
            </TabPanel>
            <TabPanel header={"Checklists"} key={"checklists"}>
              {checklistError ? (
                <div className={"error-wrapper"}>
                  <h2>Error fetching checklists</h2>
                </div>
              ) : (
                <>
                  {loadingChecklist ? (
                    <div className={"loading-wrapper"}>
                      <ProgressActivity className={"spinner"} />
                    </div>
                  ) : (
                    <TabView>
                      <TabPanel header={"Risks associated"}>
                        <div className={"checklist-wrapper"}>
                          {checklist?.risk.length === 0 ? (
                            <>
                              <p>There are no risks found for {chemicalData.name}</p>
                            </>
                          ) : (
                            <>
                              <p>
                                Read through the questions about the risks associated with{" "}
                                {chemicalData.name}
                              </p>
                              <div className={"responsive-grid-2 checklist-table-header"}>
                                <p>Question</p>
                                <p>Active</p>
                              </div>
                              {checklist?.risk.map((item, index) => {
                                return (
                                  <div className={"responsive-grid-2"} key={index}>
                                    <p>{item.question}</p>
                                    <p>
                                      {item.active ? (
                                        <CheckIcon fill={"#2dd36f"} />
                                      ) : (
                                        <CancelIcon className={"cancel-icon"} />
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </TabPanel>
                      <TabPanel header={"Safety practices"}>
                        <div className={"checklist-wrapper"}>
                          {checklist?.safe.length === 0 ? (
                            <>
                              <p>There are no safety practices for {chemicalData.name} found.</p>
                            </>
                          ) : (
                            <>
                              <p>
                                Read through the safety question before using {chemicalData.name}
                              </p>
                              <div className={"responsive-grid-2 checklist-table-header"}>
                                <p>Question</p>
                                <p>Active</p>
                              </div>
                              {checklist?.safe.map((item, index) => {
                                return (
                                  <div className={"responsive-grid-2"} key={index}>
                                    <p>{item.question}</p>
                                    <p>
                                      {item.active ? (
                                        <CheckIcon fill={"#2dd36f"} />
                                      ) : (
                                        <CancelIcon className={"cancel-icon"} />
                                      )}
                                    </p>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </div>
                      </TabPanel>
                    </TabView>
                  )}
                </>
              )}
            </TabPanel>
            <TabPanel header={"Documents"} key={"documents"}>
              <Documents
                documents={documents}
                loadingDocuments={loadingDocuments}
                errorDocuments={errorDocuments}
                data={chemicalData}
              />
            </TabPanel>
          </TabView>
        </Dialog>
      )}
      <Toast ref={toast} />
    </>
  );
}

export default ChemicalListAndChecklist;
